import {
  getPersistentSessionKey,
  getSessionKey,
  removePersistentSessionKey,
  removeSessionKey,
  setSessionKey,
  setPersistentSessionKey,
} from "../../../utils/commonfunction";

export interface GuestCountSource {
  number_of_persons?: number | string | null;
}

export interface TableDetails {
  capacity?: number | string | null;
  activeOrder?: GuestCountSource | null;
}

const VISIT_TTL_MS = 3 * 60 * 60 * 1000;
const RELOAD_DRAFT_RECOVERY_KEY = "qr_order_reload_draft_recovery";

export const cartKey = (
  restaurantId?: string | null,
  tableId?: string | null
): string => `qr_order_cart_${restaurantId || ""}_${tableId || ""}`;

export const reviewDraftKey = (
  restaurantId?: string | null,
  tableId?: string | null
): string => `qr_order_review_draft_${restaurantId || ""}_${tableId || ""}`;

const visitStampKey = (
  restaurantId?: string | null,
  tableId?: string | null
): string => `qr_order_visit_at_${restaurantId || ""}_${tableId || ""}`;

export const touchVisit = (
  restaurantId?: string | null,
  tableId?: string | null
): void => {
  setPersistentSessionKey(visitStampKey(restaurantId, tableId), String(Date.now()));
};

export const endVisit = (
  restaurantId?: string | null,
  tableId?: string | null
): void => {
  removePersistentSessionKey(cartKey(restaurantId, tableId));
  removePersistentSessionKey(reviewDraftKey(restaurantId, tableId));
  removePersistentSessionKey(visitStampKey(restaurantId, tableId));
};

export const endVisitIfUnused = (
  restaurantId?: string | null,
  tableId?: string | null,
  tableDetails?: TableDetails | null
): void => {
  if (tableDetails?.activeOrder || readVisitCart(restaurantId, tableId).length) {
    return;
  }
  endVisit(restaurantId, tableId);
};

const getNavigationType = (): string | undefined => {
  if (typeof performance === "undefined" || !performance.getEntriesByType) {
    return undefined;
  }

  return (performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined)?.type;
};

const restoreDraftAfterReload = (
  restaurantId?: string | null,
  tableId?: string | null
): void => {
  const storedRecovery = getSessionKey(RELOAD_DRAFT_RECOVERY_KEY);
  if (!storedRecovery) {
    return;
  }

  try {
    const recovery = JSON.parse(storedRecovery);
    const isSameVisit =
      String(recovery?.restaurantId || "") === String(restaurantId || "") &&
      String(recovery?.tableId || "") === String(tableId || "");

    if (getNavigationType() === "reload" && isSameVisit && recovery?.draft) {
      setPersistentSessionKey(reviewDraftKey(restaurantId, tableId), recovery.draft);
    }
  } catch {
    // A malformed recovery record should never block the QR flow.
  } finally {
    removeSessionKey(RELOAD_DRAFT_RECOVERY_KEY);
  }
};

export const endVisitOnPagehide = (
  restaurantId?: string | null,
  tableId?: string | null,
  tableDetails?: TableDetails | null
): void => {
  if (tableDetails?.activeOrder || readVisitCart(restaurantId, tableId).length) {
    return;
  }

  const draft = getPersistentSessionKey(reviewDraftKey(restaurantId, tableId));
  endVisit(restaurantId, tableId);

  if (draft) {
    setSessionKey(
      RELOAD_DRAFT_RECOVERY_KEY,
      JSON.stringify({ restaurantId, tableId, draft })
    );
  }
};

const visitIsLive = (
  restaurantId?: string | null,
  tableId?: string | null
): boolean => {
  const stampedAt = Number(
    getPersistentSessionKey(visitStampKey(restaurantId, tableId))
  );
  if (!stampedAt) {
    touchVisit(restaurantId, tableId);
    return true;
  }
  if (Date.now() - stampedAt < VISIT_TTL_MS) {
    return true;
  }
  endVisit(restaurantId, tableId);
  return false;
};

export const readVisitCart = (
  restaurantId?: string | null,
  tableId?: string | null
): unknown[] => {
  try {
    if (!visitIsLive(restaurantId, tableId)) {
      return [];
    }
    const stored = getPersistentSessionKey(cartKey(restaurantId, tableId));
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

export const saveVisitCart = (
  restaurantId: string | null | undefined,
  tableId: string | null | undefined,
  items: unknown[]
): void => {
  setPersistentSessionKey(cartKey(restaurantId, tableId), JSON.stringify(items));
  touchVisit(restaurantId, tableId);
};

export const readReviewDraft = (
  restaurantId?: string | null,
  tableId?: string | null
): GuestCountSource | null => {
  try {
    restoreDraftAfterReload(restaurantId, tableId);
    if (!visitIsLive(restaurantId, tableId)) {
      return null;
    }
    const stored = getPersistentSessionKey(reviewDraftKey(restaurantId, tableId));
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

export const saveReviewDraft = (
  restaurantId: string | null | undefined,
  tableId: string | null | undefined,
  draft: Record<string, unknown>
): void => {
  setPersistentSessionKey(
    reviewDraftKey(restaurantId, tableId),
    JSON.stringify(draft)
  );
  touchVisit(restaurantId, tableId);
};

export const saveDraftGuestCount = (
  restaurantId: string | null | undefined,
  tableId: string | null | undefined,
  count: number
): void => {
  saveReviewDraft(restaurantId, tableId, {
    ...(readReviewDraft(restaurantId, tableId) || {}),
    number_of_persons: count,
  });
};

export const knownGuestCount = (
  tableDetails?: TableDetails | null,
  draft?: GuestCountSource | null
): number =>
  Number(tableDetails?.activeOrder?.number_of_persons) ||
  Number(draft?.number_of_persons) ||
  (Number(tableDetails?.capacity) === 1 ? 1 : 0);
